import React, { useState, useEffect, useCallback, useRef } from "react";
import Sidebar from "../../layout/Sidebar";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import AdminDataTable from "../../components/common/AdminDataTable";
import { sliderImageColumns } from "../../tables/sliderImageColumns";
import { can } from "../../utils/can";

export default function Homepage() {
  const PAGE_SIZE = 10;
  const fileInputRef = useRef(null);
  const API_URL = process.env.REACT_APP_BASE_URL_API || "";
  const token = localStorage.getItem("adminToken");
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [editId, setEditId] = useState(null);
  const [filePath, setFilePath] = useState("");
  // State
  const [slider, setSlider] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Form state
  const [form, setForm] = useState({
    file: null,
    status: true,
  });

  const columns = sliderImageColumns({ handleEdit, handleDelete, PAGE_SIZE, API_URL });

  // Fetch slider images
  const fetchSlider = useCallback(async () => {
    try {
      setListLoading(true);

      const query = new URLSearchParams({
        page,
        pageSize,
        sortBy,
        sortOrder,
      });

      const res = await fetch(`${API_URL}/api/admin/slider-list?${query.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();

      if (result.success) {
        setSlider(result.data || []);
        setTotalPages(Math.ceil(result.totalCount / pageSize));
      } else {
        setSlider([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error("Fetch Slider Error:", err);
      setSlider([]);
      setTotalPages(1);
    } finally {
      setListLoading(false);
    }
  }, [API_URL, token, page, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    fetchSlider();
  }, [fetchSlider]);

  // Validation & Submit
  const handleSubmit = async (e) => {
  e.preventDefault();

  const permissionType = editId ? "edit" : "create";

  if (can("slider_image", permissionType) === false) {
    alert("You don't have permission.");
    return;
  }

  if (!form.file && !editId) {
    alert("Please select an image file.");
    setErrors({});
    return;
  }

  const allowedWidth = 1400;
  const allowedHeight = 690;

  // ✅ Validate image only if new file selected
  if (form.file) {
    const img = new Image();
    img.src = URL.createObjectURL(form.file);

    await new Promise((resolve, reject) => {
      img.onload = () => {
        if (
          img.width !== allowedWidth 
        ) {
          alert(
            `Image size must be ${allowedWidth}x${allowedHeight}px.`
          );
          reject();
        } else {
          resolve();
        }
      };
      img.onerror = reject;
    }).catch(() => {
      return;
    });
  }

  const formData = new FormData();

  if (form.file) {
    formData.append("image", form.file);
  }

  formData.append("status", form.status ? "Active" : "Deactive");

  try {
    const url = editId
      ? `${API_URL}/api/admin/slider-list/update/${editId}`
      : `${API_URL}/api/admin/slider-create`;

    const method = "POST";

    const res = await fetch(url, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const result = await res.json();

    if (result.success) {
      setMessage(
        result.message ||
          (editId
            ? "Slider updated successfully!"
            : "Slider added successfully!")
      );

      setForm({ file: null, status: true });
      setEditId(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      fetchSlider();
    } else {
      alert(result.message || "Operation failed.");
    }
  } catch (err) {
    console.error("Submit Slider Error:", err);
    alert("Server error.");
  }
};

  // Edit handler
  function handleEdit(row) {
    if (can("slider_image", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });

    setEditId(row._id);
    setFilePath(row.image);
    setForm({
      file: null,
      status: row.status === "Active",
    });
  }

  // Delete handler
  async function handleDelete(id) {
    if (can("slider_image", "delete") === false) {
      alert("You don't have permission.");
      return;
    }

    if (!window.confirm("Are you sure you want to delete this slider?")) return;

    try {
      const res = await fetch(`${API_URL}/api/admin/slider-list/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.success) {
        setMessage(result.message || "Slider deleted successfully!");
        fetchSlider();
      } else {
        alert(result.message || "Failed to delete slider.");
      }
    } catch (err) {
      console.error("Delete Slider Error:", err);
      alert("Server error.");
    }
  }

  return (
    <div className="container body">
      <div className="main_container">
        <Sidebar />
        <Header pageTitle="Slider Image" />

        <>
          {/* page content */}
          <div className="right_col" role="main">
            
            <div className="clearfix" />

            {/* Entry Form */}
            <div className="x_panel">
              <div className="x_title">
                <h2>Entry</h2>
                <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                  <li>
                    <a className="collapse-link">
                      <i className="fa fa-chevron-up" />
                    </a>
                  </li>
                </ul>
                <div className="clearfix" />
              </div>
              <div className="x_content">
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-12 col-sm-5 pr-lg-0 mb-3">
                      <label>Add Slider Image</label>
                      <input
                        type="file"
                        ref={fileInputRef}
                        className="form-control"
                        accept="image/*"
                        onChange={(e) => setForm({ ...form, file: e.target.files[0] })}
                      />

                      <em>image size in pixel ( width: 1400, height: 690 )</em>
                    </div>
                    <div className="col-12" />
                    <div className="col-12 col-sm-5 pr-lg-0">
                      <input
                        type="checkbox"
                        id="active"
                        checked={form.status}
                        onChange={(e) => setForm({ ...form, status: e.target.checked })}
                      />
                      <label htmlFor="active" className="form-label">
                        Active/Deactive
                      </label>
                    </div>
                    <div className="col-12" />
                    <div className="col-12 col-sm-5 pr-lg-0 text-right">
                      <button type="submit" className="btn sitebtn">
                        Submit
                      </button>
                      {message && <p className="mt-2" style={{ color: "green" }}>{message}</p>}
                    </div>
                  </div>
                </form>
              </div>
            </div>

            {/* List */}
            <div className="x_panel">
              <div className="x_title">
                <h2>List</h2>
                <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                  <li>
                    <a className="collapse-link">
                      <i className="fa fa-chevron-up" />
                    </a>
                  </li>
                </ul>
                <div className="clearfix" />
              </div>

              <AdminDataTable
                columns={columns}
                data={slider}
                page={page}
                pageSize={pageSize}
                totalPages={totalPages}
                loading={listLoading}
                onPageChange={setPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setPage(1);
                }}
                sortBy={sortBy}
                sortOrder={sortOrder}
              />
            </div>
          </div>
        </>

        <Footer />
      </div>
    </div>
  );
}
